import { ThemePalette } from './deriveThemePalette';

/**
 * The literal colors StateMap's MapLibre canvas paints, per section
 * background mode. Exists because a WebGL paint property can't read a CSS
 * custom property (see StateMap.tsx's `baseStyle` docstring) — every other
 * section type gets its dark/light band for free from `[data-section-bg]`
 * (ocen.css), but the map has to be told explicitly.
 *
 * The three status tiers (registered/developing/neighboring) keep their
 * `none`-mode identity — burgundy/gold-pale/paper-warm on paper — untouched:
 * that combination already reads fine and is what every existing screenshot,
 * demo, and admin's expectation is built on. 'light' and 'dark' only diverge
 * where the `none` colors would otherwise camouflage against the section's
 * own band or invert the visual hierarchy:
 *
 * - 'light': the band is a soft tint of the accent color — the same value
 *   `deriveShades(accent).soft` produces for `goldPale`, i.e. the
 *   "developing" fill. Left alone, that tier would vanish into the band, so
 *   it steps up to the more saturated `goldDeep` instead.
 * - 'dark': the band is a deep shade of the primary color, the same family
 *   `burgundy` is drawn from. Left alone, "registered" (burgundy fill) would
 *   nearly disappear into the band, and "neighboring" (paper-warm, near
 *   white) would invert the hierarchy by becoming the brightest thing on the
 *   map. Registered swaps to gold — the accent already used for its outline
 *   — and neighboring drops to a muted tint of the band itself
 *   (`mapNeighboringDark`) so it stays the least emphasized tier.
 */
export interface MapModePalette {
  /** MapLibre's single `background` layer — the canvas's "ocean". */
  canvasBackground: string;
  neighboring: string;
  developing: string;
  registered: string;
  hoverNeighboring: string;
  hoverDeveloping: string;
  hoverRegistered: string;
  outline: string;
  registeredOutline: string;
}

/** A section's `background` band, as `@kevlarcon/pages` defines it. */
export type MapBackground = 'none' | 'light' | 'dark' | 'photo';

export function deriveMapPalette(
  mode: MapBackground,
  palette: ThemePalette,
): MapModePalette {
  // A photo band carries the same light-on-dark text as a dark one (the
  // library styles `[data-section-bg=dark|photo]` together), so the map is
  // drawn for a dark band. The map section types don't opt into photo
  // backgrounds today; this keeps the type honest if one ever does.
  if (mode === 'dark' || mode === 'photo') {
    return {
      canvasBackground: palette.sectionBgDark,
      // Diverges from 'none': paper-warm (near-white) would otherwise be the
      // brightest thing on a dark canvas, inverting the hierarchy.
      neighboring: palette.mapNeighboringDark,
      developing: palette.goldPale,
      // Diverges from 'none': burgundy fill on a burgundy-deep canvas is
      // nearly invisible. Gold is the same accent its outline already uses.
      registered: palette.gold,
      hoverNeighboring: palette.gold,
      hoverDeveloping: palette.gold,
      hoverRegistered: palette.ivory,
      outline: palette.ruleSoft,
      // Must differ from `registered` (gold) now, or the outline vanishes
      // into its own fill.
      registeredOutline: palette.ivory,
    };
  }

  if (mode === 'light') {
    return {
      canvasBackground: palette.sectionBgLight,
      neighboring: palette.paperWarm,
      // Diverges from 'none': `goldPale` is the exact tint `sectionBgLight`
      // is built from, so this tier would otherwise camouflage into the band.
      developing: palette.goldDeep,
      registered: palette.burgundy,
      hoverNeighboring: palette.parchment,
      hoverDeveloping: palette.gold,
      hoverRegistered: palette.burgundyDeep,
      outline: palette.ruleSoft,
      registeredOutline: palette.gold,
    };
  }

  return {
    canvasBackground: palette.paper,
    neighboring: palette.paperWarm,
    developing: palette.goldPale,
    registered: palette.burgundy,
    hoverNeighboring: palette.parchment,
    hoverDeveloping: palette.gold,
    hoverRegistered: palette.burgundyDeep,
    outline: palette.ruleSoft,
    registeredOutline: palette.gold,
  };
}
